import React, { Fragment, memo, useMemo } from 'react';
import { GCModule, GCModuleEnum } from '@business/constants/permissions';
import { UserResponse, UserRole } from '@business/entities/account';
import { LabelNameEnum } from '@business/entities/label';
import { R } from '@core/utils/r';
import { Session } from '@modules/auth/session';
import { useObservable } from '@core/utils/hooks/rxjs';
import { PermissionService } from '.';
import { ProjectPermission } from '@business/entities/project';
import { isDataExportRestrictedAccount, isVipRestrictedAccount } from '@business/services/common_service/common_service';

export function canSeeIt(user: UserResponse, key: string) {
  if (user.role === UserRole.admin || user.permissionAll) return true;
  return R.find(user.permissions, p => p.key === key)?.value === true;
}

/**
 * Whether the user may export/download bulk data.
 *
 * Mirror of the backend `PermissionHelper::canExportData()`. Opt-in: a missing
 * permissions row means DENIED, so export must be granted explicitly.
 *
 * Unlike every other permission, the admin role does NOT bypass this check. An
 * administrator gets no export by default and can have it explicitly turned
 * off. Only `permissionAll` (Full Account Admin) bypasses, so the account can
 * never lock every user out of exporting.
 */
export function canExportData(inUser?: UserResponse | null) {
  if (!isDataExportRestrictedAccount()) return true;

  const user = inUser ?? Session.user;
  if (!user) return false;
  if (user.permissionAll) return true;

  const rows = R.filter(user.permissions || [], p => p.module === 'data-export' && p.key === 'data-export');
  if (!rows.length) return false; // opt-in: no row means no export
  const own = R.find(rows, p => String(p.userId) === String(user.id));
  if (own) return own.value === true; // individual row beats team row
  return R.some(rows, p => p.value === true); // otherwise any team grant allows
}

export function hasBilling(fn?: () => any, inUser?: UserResponse) {
  const user = inUser || Session.user;
  let value = false;
  if (!user) {
    value = false;
  } else {
    value = canSeeIt(user, 'billing');
  }

  if (fn && value) {
    return fn();
  } else {
    return value;
  }
}

export function canSeeBilling() {
  const user = Session.user;
  if (!user) {
    return false;
  }
  return canSeeIt(user, 'billing');
}

export function getLabelByModule(module: GCModule) {
  switch (module) {
    case GCModuleEnum.deals:
      return LabelNameEnum.deals;
    case GCModuleEnum.contacts:
      return LabelNameEnum.contacts;
    case GCModuleEnum.organizations:
      return LabelNameEnum.orgs;
    case GCModuleEnum.projects:
      return LabelNameEnum.projects;
    case GCModuleEnum.marketing:
      return LabelNameEnum.subs;
    case GCModuleEnum.reports:
      return LabelNameEnum.reports;
    case GCModuleEnum.people:
      return LabelNameEnum.people;
  }
  return null;
}

export function canManage(module: string, fn?: () => any, inUser?: UserResponse) {
  const user = inUser || Session.user;
  let value = false;
  if (!user) {
    value = false;
  } else {
    value = canSeeIt(user, module);
  }

  if (fn && value) {
    return fn();
  } else {
    return value;
  }
}

export function isAdminUser() {
  const user = Session.user;
  return user && user.role === UserRole.admin;
}

export function isProposalFeeEnabled() {
  const user = Session.user;
  const permission = user?.permissions.find(p => p.module === 'proposals-items' && p.key === 'modifyFeeInOrders');
  return permission?.value ? true : false;
}

export interface CanManageModuleProps {
  children: React.ReactNode;
  action: 'add' | 'edit' | 'delete';
  module: string;
}

export const CanManageModule = memo((props: CanManageModuleProps) => {
  const { action, module } = props;
  const user = useObservable(Session.user$);
  const isAdmin = useMemo(() => user && user.role === UserRole.admin, [user]);

  if (isAdmin) {
    return props.children;
  }

  return PermissionService.canManage(module, () => {
    return props.children;
  });
});

export interface CanExportDataProps {
  children: React.ReactNode;
}

/** Renders its children only when the current user may export data. */
export const CanExportData = memo((props: CanExportDataProps) => {
  const user = useObservable(Session.user$);
  if (!canExportData(user)) {
    return null;
  }
  return <Fragment>{props.children}</Fragment>;
});

export function hasPermission(module: string, action: string, user: UserResponse | null, fn?: () => any) {
  let value = false;

  if (!user) {
    value = false;
  }

  const isAdmin = user && user.role === UserRole.admin;

  if (isAdmin) {
    value = true;
  }

  const permission = R.find(user ? user.permissions : [], p => p.module === module);

  if (module === 'library' || module === 'courses' || module === 'proposal-templates') {
    if (R.includes(permission?.ids || [], 'manage')) {
      value = true;
    } else if (action && action === 'add' && !isAdmin) {
      value = false;
    }
  }

  if (user && module === 'library') {
    const ap = R.find(user.permissions, p => p.module === module);
    if (ap) {
      const newAP = { ...ap, ids: [...ap.ids] };
      const allIds: string[] = [];
      user.permissions
        .filter(p => p.module === module && p.key === 'library')
        .forEach(p => {
          allIds.push(...p.ids);
        });
      newAP.ids = R.uniq(allIds);
      if (R.includes(newAP?.ids || [], 'manage')) {
        value = true;
      } else {
        value = false;
      }
    }
  }

  if (value === false && user && module === 'forms-shared-library') {
    const ap = R.find(user.permissions, p => p.module === module);
    if (ap) {
      const newAP = { ...ap, ids: [...ap.ids] };
      const allIds: string[] = [];
      user.permissions
        .filter(p => p.module === module && p.key === 'forms-shared-library')
        .forEach(p => {
          allIds.push(...p.ids);
        });
      newAP.ids = R.uniq(allIds);
      if (R.includes(newAP?.ids || [], 'manage')) {
        value = true;
      } else {
        value = false;
      }
    }
  }

  if (fn && value) {
    return fn();
  } else {
    return value;
  }
}

export function canSeeCommission(module: string, user: UserResponse | null, userId: string) {
  let value = false;

  if (!user) {
    value = false;
  }

  const isAdmin = user && user.role === UserRole.admin;

  if (isAdmin) {
    value = true;
  }

  const permission = R.find(user ? user.permissions : [], p => p.module === module);

  if (user && module === 'commissions') {
    if (R.includes(permission?.ids || [], '-1')) {
      value = userId === user.id;
    } else {
      value = true;
    }
  }
  return value;
}

export function getProjectPermissionLabel(p: ProjectPermission) {
  switch (p) {
    case ProjectPermission.view:
      return 'View';
    case ProjectPermission.ownManage:
      return 'Add/Edit/Delete their Own Steps';
    case ProjectPermission.allManage:
      return 'View/Add/Edit/Delete All Steps';
  }
}

export function getProjectPermissionMessage(p: ProjectPermission) {
  switch (p) {
    case ProjectPermission.view:
      return 'Are you sure you want to share the whole project? This means your client can see all the sections and steps of the project?';
    case ProjectPermission.ownManage:
      return 'Are you sure you want to share the whole project? This means your client can see all the sections and steps of the project and add, edit and delete their own steps.';
    case ProjectPermission.allManage:
      return 'Are you sure you want to share the whole project? This means your client can view, add, edit and delete all sections and steps of the project.';
  }
}

export function canAccessModule(module: string, user: UserResponse | null) {
  let value = false;

  if (!user) {
    value = false;
  }

  const isAdmin = user && user.role === UserRole.admin;

  if (isAdmin) {
    return true;
  }

  const permission = getUserPermissionByKey(module, user);

  if (!permission) {
    return true;
  }

  if (permission) {
    value = permission.value;
  }

  return value;
}

export function getUserPermissionByKey(module: string, user: UserResponse | null) {
  return R.find(user?.permissions || [], p => p.key === module);
}

export const VIP_PERMISSION_KEY = 'vip';

/**
 * Can this user see sensitive info (phone / email / address / work orders)
 * on organizations flagged VIP?
 *
 * Default-on: when no permissions row exists the user is allowed. Only an
 * explicit row with value === false restricts. Mirrors canAccessModule().
 */
export function canSeeVipInfo(inUser?: UserResponse | null) {
  const user = inUser || Session.user;
  if (!user) return false;
  if (user.role === UserRole.admin || user.permissionAll) return true;
  const rows = R.filter(user.permissions || [], p => p.module === GCModuleEnum.organizations && p.key === VIP_PERMISSION_KEY);
  if (!rows.length) return true; // default-on preserved
  const own = R.find(rows, p => String(p.userId) === String(user.id));
  if (own) return own.value === true; // individual row beats team row
  return !R.some(rows, p => p.value === false); // any team deny restricts
}

/**
 * True when this organization's sensitive info must be hidden from the current user.
 * Restricted to the accounts named by isVipRestrictedAccount() — every other
 * account keeps the pre-feature behaviour.
 */
export function isVipRestricted(org?: { isVip?: boolean } | null, inUser?: UserResponse | null) {
  if (!isVipRestrictedAccount()) return false;
  if (!org?.isVip) return false;
  return !canSeeVipInfo(inUser);
}

/** Reactive form of isVipRestricted() — re-evaluates when the session user changes. */
export function useVipRestricted(org?: { isVip?: boolean } | null) {
  const user = useObservable(Session.user$);
  return useMemo(() => isVipRestricted(org, user), [org, user]);
}
